import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setPageMeta } from '../lib/seo';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);

  const orderId = searchParams.get('orderId');
  const paymentId = searchParams.get('paymentId');

  const MAX_ATTEMPTS = 30;

  const checkPaymentStatus = useCallback(async (attempt = 0) => {
    try {
      const endpoint = paymentId
        ? `/api/payments/status/${paymentId}`
        : `/api/payments/status-by-order/${orderId}`;
      const response = await axios.get(endpoint);
      const nextPayment = response.data;
      setPayment(nextPayment);

      if (!nextPayment) {
        return;
      }

      if (nextPayment.status === 'PENDING' || nextPayment.status === 'PROCESSING') {
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
          return;
        }
        // Stop auto polling, keep page on success screen with pending status.
        return;
      }

      if (['DECLINED', 'CANCELLED', 'REFUNDED'].includes(nextPayment.status)) {
        const errorQuery = paymentId
          ? `paymentId=${paymentId}${orderId ? `&orderId=${orderId}` : ''}`
          : `orderId=${orderId}`;
        navigate(`/payment/error?${errorQuery}`, { replace: true });
        return;
      }
      if (nextPayment.status !== 'APPROVED') return;

      // Update user credits in localStorage
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        user.credits = (parseFloat(user.credits) || 0) + parseFloat(nextPayment.amount);
        localStorage.setItem('user', JSON.stringify(user));
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate, orderId, paymentId]);

  useEffect(() => {
    if (orderId || paymentId) {
      checkPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [orderId, paymentId, checkPaymentStatus]);

  useEffect(() => {
    setPageMeta({
      title: 'Payment Successful — Elorimi',
      description: 'Your Elorimi payment was processed successfully.',
    });
  }, []);

  return (
    <div className="payment-success-section">
      {/* Animated Background */}
      <div className="bg-animation">
        <div className="bg-mesh"></div>
        <div className="bg-grid"></div>
        <div className="bg-glow bg-glow-1"></div>
        <div className="bg-glow bg-glow-2"></div>
        <div className="bg-glow bg-glow-3"></div>
      </div>

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="payment-result-card glass">
              <div className="result-icon success">
                <i className="fas fa-check"></i>
              </div>

              <h2 className="result-title">Payment Successful!</h2>
              
              <p className="result-message">
                Your payment has been processed successfully. Your order has been added to your account.
              </p>

              {loading ? (
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Verifying payment...</span>
                </div>
              ) : null}

              <div className="result-actions">
                {(payment?.status === 'PENDING' || payment?.status === 'PROCESSING') ? (
                  <button className="btn btn-primary btn-lg" onClick={() => checkPaymentStatus(0)}>
                    Check Again
                  </button>
                ) : (
                  <Link to="/dashboard" className="btn btn-primary btn-lg">
                    Go to Dashboard
                  </Link>
                )}
              </div>

              <div className="result-footer">
                <p>Need help? <a href="/contact">Contact Support</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .payment-success-section {
          padding: 120px 20px 60px;
          position: relative;
          min-height: 100vh;
        }

        .payment-result-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: var(--radius-xl);
          padding: 3rem 2rem;
          text-align: center;
          max-width: 500px;
          margin: 0 auto;
        }

        .result-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          font-size: 2rem;
        }

        .result-icon.success {
          background: rgba(39, 174, 96, 0.1);
          border: 2px solid #27ae60;
          color: #27ae60;
        }

        .result-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--white);
          margin-bottom: 1rem;
        }

        .result-message {
          color: var(--gray-600);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          color: var(--gray-600);
        }

        .loading-spinner i {
          font-size: 2rem;
          color: var(--primary);
        }

        .payment-details {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          margin-bottom: 2rem;
          text-align: left;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-row .label {
          color: var(--gray-600);
          font-size: 0.9rem;
        }

        .detail-row .value {
          color: var(--white);
          font-weight: 500;
        }

        .detail-row .value.highlight {
          color: var(--primary);
          font-weight: 700;
          font-size: 1.1rem;
        }

        .detail-row .value.status-approved {
          color: #27ae60;
          text-transform: uppercase;
          font-size: 0.8rem;
        }

        .detail-row .value.status-pending {
          color: #f39c12;
          text-transform: uppercase;
          font-size: 0.8rem;
        }

        .result-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .result-actions .btn {
          width: 100%;
          justify-content: center;
        }

        .result-footer {
          color: var(--gray-700);
          font-size: 0.85rem;
          line-height: 1.6;
        }

        .result-footer a {
          color: var(--primary);
          text-decoration: none;
        }

        .result-footer a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

export default PaymentSuccess;
